import { all, call, fork, put, select, takeEvery, throttle } from "redux-saga/effects";
import { v4 as uuid } from "uuid";

import { actions as cdActions, models as cdModels } from "~/customer-data";
import { setApiResult } from "~/core/api/actions";
import { getTheUserCompanyGuid, getTheUserGuid } from "~/login/selectors";
import { actions as notificationActions, MSGTYPE } from "~/notifications";
import {
    AgBytesAPI,
    AppHelpers,
    CustomerAPI,
    SearchAPI,
    EquipmentProfileAPI,
    APIError,
    RecAPI,
} from "@ai360/core";

import { messages } from "../../i18n-messages";
import * as actions from "./actions";
import { AgvanceUtils } from "../agvance-utils";
import fileSaver from "file-saver";
import { getInitialAgvanceCustomers } from "~/admin/setup/customer/data/selectors";

export function* fetchCustomerRequest(action) {
    const { payload } = action;
    if (payload) {
        const UserGuid = yield select(getTheUserGuid);
        try {
            const response = yield CustomerAPI.getCustomer({ UserGuid, Model: payload });
            if (response) {
                yield put(actions.fetchCustomerSuccess(response));
            } else {
                yield put(actions.fetchCustomerFailure("No Data"));
            }
        } catch (error) {
            yield put(actions.fetchCustomerFailure({ error }));
            yield put(notificationActions.apiCallError(error, action));
        }
    } else {
        yield put(actions.fetchCustomerFailure("No Data"));
    }
}

export function* fetchAgvanceCustomerRequest(action) {
    const { orgLevelGuidList, filterData = 0 } = action.payload;

    // use selector like the userGuid to check inital customer list, don't update if it exists
    if (action.payload) {
        const UserGuid = yield select(getTheUserGuid);
        const initialAgvanceCustomerList = yield select(getInitialAgvanceCustomers);
        const requestOptions = {
            UserGuid,
            Model: {
                orgLevelGuidList: orgLevelGuidList,
                filterData,
            },
        };
        try {
            const response = yield CustomerAPI.getAgvanceCustomerList(requestOptions);
            if (response) {
                if (response.isListOutOfBound) {
                    yield put(
                        actions.setAgvanceCustomerListOutOfBound({
                            flag: true,
                            count: response.listCount,
                        })
                    );
                    yield put(
                        actions.fetchAgvanceCustomerSuccess({
                            agvanceCustomers: [],
                        })
                    );
                    if (initialAgvanceCustomerList == null) {
                        yield put(
                            actions.setAgvanceInitialCustomers({
                                initialAgvanceCustomers: [],
                            })
                        );
                    }
                } else {
                    yield put(
                        actions.fetchAgvanceCustomerSuccess({
                            agvanceCustomers: response.agvCustomerList,
                        })
                    );
                    if (initialAgvanceCustomerList == null) {
                        yield put(
                            actions.setAgvanceInitialCustomers({
                                initialAgvanceCustomers: response.agvCustomerList,
                            })
                        );
                    }
                }
            }
        } catch (error) {
            yield put(actions.fetchCustomerFailure({ error }));
            yield put(notificationActions.apiCallError(error, action));
        }
    }
}

export function* createCustomerRequest(action) {
    const { payload } = action;
    if (payload) {
        const UserGuid = yield select(getTheUserGuid);
        const requestOptions = { UserGuid, Model: payload };
        try {
            const addCustomerResponse = yield call(CustomerAPI.addCustomer, requestOptions);
            const customerFieldsResponse = yield call(SearchAPI.getCustomerFields, {
                userGuid: UserGuid,
                customerGuid: [addCustomerResponse.customerGuid],
                pageSize: 1,
            });
            yield put(
                cdActions.addUpdateCustomer(
                    cdModels.CustomerInfo.fromCustomerField(customerFieldsResponse.results[0])
                )
            );
            if (addCustomerResponse) {
                yield put(actions.fetchCustomerSuccess(addCustomerResponse));
            }
            yield put(actions.createCustomerSuccess());
            yield put(actions.createCustomerOracleIdSuccess());
        } catch (error) {
            if (error instanceof APIError) {
                yield put(setApiResult(error));
            }
            yield put(actions.createCustomerFailure({ error }));
            yield put(actions.createCustomerOracleIdFailure("No Data"));
        }
    } else {
        yield put(actions.createCustomerFailure("No Data"));
        yield put(actions.createCustomerOracleIdFailure("No Data"));
    }
}

export function* customerExistsRequest(action) {
    const { payload } = action;
    if (payload) {
        const UserGuid = yield select(getTheUserGuid);
        const requestOptions = { UserGuid, Model: payload };
        try {
            const response = yield call(CustomerAPI.customerNameExists, requestOptions);
            if (response) {
                yield put(actions.fetchCustomerExistsSuccess(response.exists));
            }
        } catch (error) {
            if (error instanceof APIError) {
                yield put(setApiResult(error));
            }
            yield put(actions.fetchCustomerExistsFailure({ error }));
        }
    } else {
        yield put(actions.fetchCustomerExistsFailure("No Data"));
    }
}

export function* createCustomerOracleIdRequest(action) {
    const { payload } = action;
    if (payload) {
        const UserGuid = yield select(getTheUserGuid);
        const requestOptions = { UserGuid, Model: payload };
        try {
            const response = yield call(CustomerAPI.AddCustomerOracleIdCheck, requestOptions);

            if (response.exists) {
                //Set the oracle customer so we can update it instead of adding a new one.
                yield put(actions.setOracleCustomer(response));
                yield put(actions.createCustomerOracleIdSuccess());
            } else {
                yield put(actions.createCustomer(payload));
            }
        } catch (error) {
            if (error instanceof APIError) {
                yield put(setApiResult(error));
            }
            yield put(actions.createCustomerOracleIdFailure({ error }));
        }
    } else {
        yield put(actions.createCustomerOracleIdFailure("No Data"));
    }
}

export function* updateCustomerRequest(action) {
    const { payload } = action;
    if (payload) {
        const UserGuid = yield select(getTheUserGuid);
        const requestOptions = { UserGuid, Model: payload };
        try {
            const { myAgDataRegistrations } = payload;
            delete payload.myAgDataRegistrations;
            const updateCustomerResponse = yield call(CustomerAPI.updateCustomer, requestOptions);

            if (updateCustomerResponse && myAgDataRegistrations !== null) {
                const emailsWereSent = yield call(
                    CustomerAPI.updateMyAgDataRegistrations,
                    UserGuid,
                    payload.customerGuid,
                    myAgDataRegistrations?.map((x) => ({
                        ...x,
                        myAgDataRegistrationGuid: x.myAgDataRegistrationGuid ?? uuid(),
                    })) ?? []
                );
                if (emailsWereSent === true) {
                    yield put(
                        notificationActions.pushToasterMessage(
                            messages.myAgDataEmailSent,
                            MSGTYPE.INFO
                        )
                    );
                }
            }

            const customerGuid = updateCustomerResponse.customerGuid;
            const customerFieldsResponse = yield call(SearchAPI.getCustomerFields, {
                active: updateCustomerResponse.activeYn,
                userGuid: UserGuid,
                customerGuid: [customerGuid],
                pageSize: 1,
            });
            if (
                customerFieldsResponse.results != null &&
                customerFieldsResponse.results.length > 0
            ) {
                yield put(
                    cdActions.addUpdateCustomer(
                        cdModels.CustomerInfo.fromCustomerField(customerFieldsResponse.results[0])
                    )
                );
            }

            // If customer was deactivated as part of the customer edit/update, then deactivate the
            // customer clientside.
            if (!updateCustomerResponse.activeYn) {
                yield put(cdActions.deactivateCustomer(customerGuid));
            }

            yield put(actions.updateCustomerSuccess());
        } catch (error) {
            if (error instanceof APIError) {
                yield put(setApiResult(error));
            }
            yield put(actions.updateCustomerFailure({ error }));
        }
    } else {
        yield put(actions.updateCustomerFailure("No Data"));
    }
}

export function* processCreateEquipment(action) {
    const { payload } = action;
    const UserGuid = yield select(getTheUserGuid);
    const requestOptions = { UserGuid: UserGuid, Model: payload };
    try {
        const response = yield EquipmentProfileAPI.createEquipmentProfile(requestOptions);
        yield put(actions.createEquipmentSuccess(response));
    } catch (error) {
        if (error instanceof APIError) {
            yield put(setApiResult(error));
        }
        console.warn(
            `Failed to process action ${
                actions.createEquipment("").type
            } with payload ${JSON.stringify(payload)}`
        );
        yield put(
            notificationActions.apiCallError(
                error,
                actions.createEquipment,
                messages.failedCreateEquipment,
                { equipmentType: actions.createEquipment("").type }
            )
        );
        yield put(actions.createEquipmentFailed(error));
    }
}

export function* requestCustomerEquipmentList(action) {
    const { payload } = action;
    if (payload) {
        const UserGuid = yield select(getTheUserGuid);
        const requestOptions = { UserGuid, Model: payload };
        try {
            const response = yield EquipmentProfileAPI.getOwnerOperatorEquipmentList(
                requestOptions
            );
            yield put(actions.fetchCustomerEquipmentListSuccess(response));
        } catch (error) {
            if (error instanceof APIError) {
                yield put(setApiResult(error));
            }
            yield put(actions.fetchCustomerEquipmentListFailed({ error }));
            yield put(notificationActions.apiCallError(error, action));
        }
    } else {
        yield put(actions.fetchCustomerEquipmentListFailed("No Data"));
    }
}

export function* requestCustomerJdOrgs(action) {
    const { payload } = action;
    if (payload) {
        const UserGuid = yield select(getTheUserGuid);
        const request = {
            orgLevelGuid: payload.orgLevelGuid,
            customerGuid: payload.customerGuid,
        };
        try {
            const response = yield RecAPI.getJdOrganizationsForCustomer(request, UserGuid);
            yield put(actions.fetchCustomerJdOrgsSuccess(response));
        } catch (error) {
            if (error instanceof APIError) {
                yield put(setApiResult(error));
            }
            yield put(notificationActions.apiCallError(error, action));
        }
    }
}

export function* requestOwnerCommunityData(action) {
    const { payload } = action;
    if (payload) {
        const UserGuid = yield select(getTheUserGuid);
        const requestOptions = { UserGuid, Model: payload.model };
        try {
            const response = yield CustomerAPI.getOwnerCommunityData(requestOptions);
            yield put(actions.fetchOwnerCommunityDataSuccess(response));
        } catch (error) {
            if (error instanceof APIError) {
                yield put(setApiResult(error));
            }
            yield put(actions.fetchOwnerCommunityDataFailed({ error }));
            yield put(notificationActions.apiCallError(error, action));
        }
    } else {
        yield put(actions.fetchOwnerCommunityDataFailed("No Data"));
    }
}

export function* requestGetAgvanceCustomer(action) {
    const { payload } = action;
    const UserGuid = yield select(getTheUserGuid);
    const requestOptions = { UserGuid, AgvanceCustomerGuid: payload };
    try {
        const response = yield CustomerAPI.getAgvanceCustomer(requestOptions);
        // Parse to replace - agvanceGuid to agvanceCustomerGuid
        const editedResponse = {
            ...response,
            agvanceCustomerGuid: response.agvanceGuid,
        };
        yield put(actions.getAgvanceCustomerSuccess(editedResponse));
    } catch (error) {
        if (error instanceof APIError) {
            yield put(setApiResult(error));
        }
        yield put(actions.getAgvanceCustomerFailure({ error }));
        yield put(notificationActions.apiCallError(error, action));
    }
}

export function* requestSalespersonList(action) {
    const { payload } = action;
    if (payload) {
        const userGuid = yield select(getTheUserGuid);
        try {
            const response = yield CustomerAPI.getCustomerSalespersonList(userGuid);
            yield put(actions.fetchSalespersonListSuccess(response));
        } catch (error) {
            if (error instanceof APIError) {
                yield put(setApiResult(error));
            }
            yield put(actions.fetchSalespersonListFailed({ error }));
            yield put(notificationActions.apiCallError(error, action));
        }
    } else {
        yield put(actions.fetchSalespersonListFailed("No Data"));
    }
}

export function* sendAuthenticationRequest(action) {
    const { enrollmentEmail, mobilePhone, signerName, customerGuid } = action.payload;
    const userGuid = yield select(getTheUserGuid);
    try {
        const response = yield CustomerAPI.sendAuthentication(userGuid, {
            enrollmentEmail,
            mobilePhone,
            signerName,
            customerGuid,
        });
        yield put(actions.sendAuthenticationSuccess(response));
        yield put(
            notificationActions.pushToasterMessage(
                messages.enrollmentSendEmailSuccess,
                MSGTYPE.SUCCESS
            )
        );
    } catch (error) {
        if (error instanceof APIError) {
            yield put(setApiResult(error));
        } else {
            yield put(notificationActions.apiCallError(error, action));
        }
        yield put(actions.sendAuthenticationFailed({ error }));
    }
}

export function* updateEnrollmentFormRequest(action) {
    const userGuid = yield select(getTheUserGuid);
    const { enrollmentForm, enrollmentFormType, customerRecord } = action.payload;
    try {
        const response = yield CustomerAPI.updateEnrollmentForm(userGuid, {
            enrollmentForm,
            enrollmentFormType,
            customerGuid: customerRecord.customerGuid,
        });
        const updatedCustomer = {
            ...customerRecord,
            enrolledFormUrl: response,
        };
        yield put(
            actions.updateEnrollmentFormSuccess({
                customerRecord: updatedCustomer,
            })
        );
        yield put(
            notificationActions.pushToasterMessage(
                messages.enrollmentFormUploadSuccess,
                MSGTYPE.SUCCESS
            )
        );
    } catch (error) {
        if (error instanceof APIError) {
            yield put(setApiResult(error));
        } else {
            yield put(notificationActions.apiCallError(error, action));
        }
        yield put(actions.updateEnrollmentFormFailed({ error }));
    }
}

export function* fetchEnrollmentFormRequest(action) {
    const userGuid = yield select(getTheUserGuid);
    const { customerName, enrolledFormUrl } = action.payload;

    try {
        const response = yield CustomerAPI.getEnrollmentForm(userGuid, enrolledFormUrl);
        const fileType = enrolledFormUrl.split(".").pop();
        fileSaver.saveAs(
            AppHelpers.base64ToBlob(response),
            customerName + "_EnrollmentAgreement." + fileType
        );
        yield put(actions.fetchEnrollmentFormSuccess(response));
    } catch (error) {
        if (error instanceof APIError) {
            yield put(setApiResult(error));
        } else {
            yield put(notificationActions.apiCallError(error, action));
        }
        yield put(actions.fetchEnrollmentFormFailed({ error }));
    }
}

function* fetchMyAgDataRegistrationRequest(action) {
    const userGuid = yield select(getTheUserGuid);

    try {
        const response = yield CustomerAPI.getMyAgDataRegistration(userGuid, action.payload);
        yield put(actions.fetchMyAgDataRegistrationSuccess(response));
    } catch (error) {
        if (error instanceof APIError) {
            yield put(setApiResult(error));
        } else {
            yield put(notificationActions.apiCallError(error, action));
        }
        yield put(actions.fetchMyAgDataRegistrationFailed({ error }));
    }
}

export function* fetchAgvanceCustomerWithNameSearch(action) {
    const {
        nameSearch,
        orgLevelGuidList,
        isAdditionalAgvanceCustomer = false,
        filterData = 0,
    } = action.payload;
    if (nameSearch?.length >= AgvanceUtils.AGVANCE_CUSTOMER_SEARCH_AUTOCOMPLETE_LIMIT) {
        const UserGuid = yield select(getTheUserGuid);
        const requestOptions = {
            UserGuid,
            Model: {
                nameSearch,
                orgLevelGuidList,
                filterData,
            },
        };
        try {
            yield put(actions.setCustomerSearchIsLoading(true));
            yield put(actions.setAdditionalAgvanceCustomerFlag(isAdditionalAgvanceCustomer));
            const response = yield CustomerAPI.getAgvanceCustomerListWithNameSearch(requestOptions);
            yield put(
                actions.fetchAgvanceCustomerSuccess({
                    agvanceCustomers: response.agvCustomerList,
                    isAdditionalAgvanceCustomer,
                })
            );
            yield put(actions.setCustomerSearchIsLoading(false));
        } catch (error) {
            yield put(actions.setCustomerSearchIsLoading(false));
            if (error instanceof APIError) {
                yield put(setApiResult(error));
            }
            yield put(actions.fetchAgvanceCustomerWithNameSearchFailed({ error }));
            yield put(notificationActions.apiCallError(error, action));
        }
    } else {
        yield put(actions.resetAgvanceCustomers());
        yield put(actions.setCustomerSearchIsLoading(false));
    }
}

export function* throttleAgvanceCustomerRequest() {
    yield throttle(
        1000,
        actions.fetchAgvanceCustomerWithNameSearch,
        fetchAgvanceCustomerWithNameSearch
    );
}

export function* watchUpdateCustomer() {
    yield takeEvery(actions.updateCustomer, updateCustomerRequest);
}

export function* watchFetchCustomer() {
    yield takeEvery(actions.fetchCustomer, fetchCustomerRequest);
}

export function* watchFetchAgvanceCustomer() {
    yield takeEvery(actions.fetchAgvanceCustomer, fetchAgvanceCustomerRequest);
}

export function* watchCreateCustomer() {
    yield takeEvery(actions.createCustomer, createCustomerRequest);
}

export function* watchCreateCustomerOracleId() {
    yield takeEvery(actions.createCustomerOracleId, createCustomerOracleIdRequest);
}

export function* watchCreateEquipment() {
    yield takeEvery(actions.createEquipment, processCreateEquipment);
}

export function* watchFetchCustomerEquipmentList() {
    yield takeEvery(actions.fetchCustomerEquipmentList, requestCustomerEquipmentList);
}

export function* watchFetchCustomerExists() {
    yield takeEvery(actions.fetchCustomerExists, customerExistsRequest);
}

export function* watchFetchCustomerJdOrgs() {
    yield takeEvery(actions.fetchCustomerJdOrgs, requestCustomerJdOrgs);
}

export function* watchFetchOwnerCommunityData() {
    yield takeEvery(actions.fetchOwnerCommunityData, requestOwnerCommunityData);
}

export function* watchGetAgvanceCustomer() {
    yield takeEvery(actions.getAgvanceCustomer, requestGetAgvanceCustomer);
}

export function* watchFetchSalespersonList() {
    yield takeEvery(actions.fetchSalespersonList, requestSalespersonList);
}

export function* watchSendAuthentication() {
    yield takeEvery(actions.sendAuthentication, sendAuthenticationRequest);
}

export function* watchupdateEnrollmentForm() {
    yield takeEvery(actions.updateEnrollmentForm, updateEnrollmentFormRequest);
}

export function* watchFetchEnrollmentForm() {
    yield takeEvery(actions.fetchEnrollmentForm, fetchEnrollmentFormRequest);
}

export function* watchFetchMyAgDataRegistration() {
    yield takeEvery(actions.fetchMyAgDataRegistration, fetchMyAgDataRegistrationRequest);
}

function* onProductLinkageCheckDegraded(action) {
    const { customerGuid, orgLevelGuid } = action.payload ?? {};
    const companyGuid = yield select(getTheUserCompanyGuid);
    // Owner-level org levels never carry their own Agvance credentials, so a degraded check here
    // is expected, not a real failure -- skip the toast when no location/customer was resolved.
    if (!customerGuid && orgLevelGuid === companyGuid) {
        return;
    }
    yield put(
        notificationActions.pushToasterMessage(
            "Failed to verify linked products. All products currently being displayed.",
            MSGTYPE.WARNING
        )
    );
}

export function* watchProductLinkageCheckDegraded() {
    yield takeEvery(AgBytesAPI.PRODUCT_LINKAGE_CHECK_DEGRADED, onProductLinkageCheckDegraded);
}

// Message per agbytes `linkageDegradedReason`; null for an unknown or missing code.
function getLinkageDegradedMessage(reason, things) {
    const fullList = `All ${things} are being displayed.`;
    switch (reason) {
        case "multipleDatabases":
            return `The selected customers/locations are in more than one Agvance database, so linked ${things} can't be filtered. ${fullList}`;
        case "multipleCompanies":
            return `The selected customers/locations are in more than one company, so linked ${things} can't be filtered. ${fullList}`;
        case "multipleOwners":
            return `The selected customers/locations are set up differently in Agvance, so linked ${things} can't be filtered. ${fullList}`;
        case "loginFailed":
            return `Couldn't log in to Agvance for the selected customer/location, so linked ${things} can't be filtered. ${fullList}`;
        case "noCredentials":
            return `No Agvance login is set up for the selected customer/location, so linked ${things} can't be filtered. ${fullList}`;
        case "error":
            return `Failed to verify linked ${things}. All ${things} currently being displayed.`;
        default:
            return null;
    }
}

function* pushCropLinkageDegradedMessage(payload, things) {
    const { customerGuid, orgLevelGuid, customerGuids, orgLevelGuids, reason } = payload ?? {};
    const reasonMessage = getLinkageDegradedMessage(reason, things);
    // Several customers/locations can't be filtered; explain the full list.
    if (customerGuids?.length || orgLevelGuids?.length) {
        yield put(
            notificationActions.pushToasterMessage(
                reasonMessage ??
                    `Linked ${things} can't be filtered for the selected customers/locations. All ${things} are being displayed.`,
                MSGTYPE.WARNING
            )
        );
        return;
    }
    const companyGuid = yield select(getTheUserCompanyGuid);
    // Owner-level org levels never carry their own Agvance credentials, so a degraded check here
    // is expected, not a real failure -- skip the toast when no location/customer was resolved.
    if (!customerGuid && orgLevelGuid === companyGuid) {
        return;
    }
    yield put(
        notificationActions.pushToasterMessage(
            reasonMessage ??
                `Failed to verify linked ${things}. All ${things} currently being displayed.`,
            MSGTYPE.WARNING
        )
    );
}

function* onCropLinkageCheckDegraded(action) {
    yield* pushCropLinkageDegradedMessage(action.payload, "crops");
}

export function* watchCropLinkageCheckDegraded() {
    yield takeEvery(AgBytesAPI.CROP_LINKAGE_CHECK_DEGRADED, onCropLinkageCheckDegraded);
}

function* onCropPurposeLinkageCheckDegraded(action) {
    yield* pushCropLinkageDegradedMessage(action.payload, "crop purposes");
}

export function* watchCropPurposeLinkageCheckDegraded() {
    yield takeEvery(
        AgBytesAPI.CROP_PURPOSE_LINKAGE_CHECK_DEGRADED,
        onCropPurposeLinkageCheckDegraded
    );
}

const CustomerSagas = function* () {
    yield all([
        fork(watchFetchCustomer),
        fork(watchFetchAgvanceCustomer),
        fork(watchCreateCustomer),
        fork(watchCreateCustomerOracleId),
        fork(watchUpdateCustomer),
        fork(watchCreateEquipment),
        fork(watchFetchCustomerEquipmentList),
        fork(watchFetchCustomerExists),
        fork(watchFetchCustomerJdOrgs),
        fork(watchFetchOwnerCommunityData),
        fork(watchFetchEnrollmentForm),
        fork(watchupdateEnrollmentForm),
        fork(watchGetAgvanceCustomer),
        fork(watchFetchSalespersonList),
        fork(watchSendAuthentication),
        fork(throttleAgvanceCustomerRequest),
        fork(watchFetchMyAgDataRegistration),
        fork(watchProductLinkageCheckDegraded),
        fork(watchCropLinkageCheckDegraded),
        fork(watchCropPurposeLinkageCheckDegraded),
    ]);
};

export default CustomerSagas;
